import type React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Card, Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { TONE_TEXT, type OverviewTone } from '../overview/overviewMeta';

type StatTileProps = {
    /** Literal testid prefix, e.g. `balance-working-capital`. */
    slot: string;
    icon: string;
    label: string;
    value: string;
    /** Tints the value where the figure carries its own verdict. */
    tone?: OverviewTone | null;
    /** A one-word reading of the value, e.g. "very good". */
    verdict?: { label: string; tone: OverviewTone } | null;
    /** Where the chevron goes; absent on a tile that only reports. */
    href?: string | null;
    /** Accessible name for the chevron — "Total assets" alone is not an action. */
    linkLabel?: string;
};

/**
 * One figure in the Balance Picture strip: what it is, what it reads, and — on
 * the three that drill — where to go and look at the detail.
 *
 * The label sits above the value, not beside it, so five tiles of different
 * lengths still scan as one row of numbers.
 */
export const StatTile: React.FC<StatTileProps> = ({
    slot,
    icon,
    label,
    value,
    tone,
    verdict,
    href,
    linkLabel,
}) => (
    <Card variant="secondary" {...getNodeTestIdProp(`${slot}-tile`)}>
        <Card.Content className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2">
                    <Icon
                        aria-hidden
                        className="shrink-0 text-muted"
                        icon={icon}
                        width={16}
                    />
                    <span className="truncate text-sm text-muted">{label}</span>
                </span>
                {href ? (
                    <Link
                        aria-label={linkLabel}
                        className="shrink-0 text-muted"
                        to={href}
                        {...getNodeTestIdProp(`${slot}-link`)}
                    >
                        <Icon aria-hidden icon="gravity-ui:chevron-right" />
                    </Link>
                ) : null}
            </div>
            <span
                className={`text-2xl font-semibold ${tone ? TONE_TEXT[tone] : 'text-foreground'}`}
                {...getNodeTestIdProp(`${slot}-value`)}
            >
                {value}
            </span>
            {verdict ? (
                <span>
                    <Chip
                        color={verdict.tone}
                        {...getNodeTestIdProp(`${slot}-verdict`)}
                    >
                        <Chip.Label>{verdict.label}</Chip.Label>
                    </Chip>
                </span>
            ) : null}
        </Card.Content>
    </Card>
);
